/* =========================================================================
 * IBC 2.0 — PRODUCT ENGINE
 * -------------------------------------------------------------------------
 * Two custom elements:
 *
 *   <ibc-media-gallery>  the product gallery. Public API:
 *                          gallery.setMedia([mediaId, …])   swap the frames
 *                          gallery.goToMediaId(mediaId)
 *                          gallery.getMediaIds()
 *                          gallery.next() / gallery.prev()
 *
 *   <ibc-product>        owns selection state for the product page. It reads
 *                        ONE source of truth — the JSON island written by
 *                        snippets/ibc-product-json.liquid — and never scrapes
 *                        the DOM for prices, handles or ids.
 *
 * WHAT THE SECOND AGENT NEEDS (add-ons, personalisation, gift box)
 * ----------------------------------------------------------------
 * 1. Selection changes:
 *
 *      document.addEventListener('ibc:product-selection', function (e) {
 *        e.detail.productId;   // the SELECTED product (may be a linked one)
 *        e.detail.variantId;
 *        e.detail.product;     // the whole node from the JSON island
 *        e.detail.variant;
 *      });
 *
 *    Fired on every change and once on first render, from <ibc-product>,
 *    bubbling to document. Add-ons must always be read from
 *    `detail.product`, never from the page's own product.
 *
 * 2. Contributing to add to basket, WITHOUT editing this file:
 *
 *      window.IbcProduct.addSubmitHook(function (ctx) {
 *        ctx.selection;    // { productId, variantId, product, variant }
 *        ctx.quantity;     // the parent quantity
 *        ctx.properties;   // parent line-item properties — mutate freely
 *        ctx.items;        // push child line items: { id, quantity, properties }
 *        ctx.skipParent;   // set true to add ONLY ctx.items
 *        return { error: 'Choose 25 pieces first.' };   // aborts the add
 *      });
 *
 *    `ctx.skipParent` exists for the multipack, whose product is a page and
 *    a builder rather than something anybody buys: its pieces go into the
 *    basket as their own lines at their own prices, and the pack product's
 *    own price is never charged. Setting it drops the parent from the POST
 *    and nothing else changes — the busy state, the error region, the focus
 *    handling and the `ibc:cart-changed` dispatch are all still this file's.
 *    An add that would post nothing at all is refused rather than sent.
 *
 *    Support for it is advertised on `window.IbcProduct.capabilities`, so a
 *    caller can tell an updated copy of this file from an older one instead
 *    of assuming, and do something safe when the answer is no.
 *
 *    Hooks may be async. They run in registration order, before the POST.
 *    Returning `{ error: '…' }` (or throwing) aborts and shows the message
 *    in the inline error region. `window.IbcProduct.removeSubmitHook(fn)`
 *    unregisters. `window.IbcProduct.getSelection()` reads the current
 *    selection at any time.
 *
 * 3. The add-ons region: <ibc-product> keeps `[data-ibc-addons-region]`'s
 *    `data-selected-product-id` in step with the selection, and shows the
 *    child matching `[data-addons-for-product="<id>"]`, hiding the rest.
 *
 * On every successful add this file dispatches `ibc:cart-changed` on
 * `document` with the whole cart on `detail.cart` plus `item_count`,
 * `items` and `total_price` at the top level — the same shape
 * assets/ibc-quick-add.js uses, which is what the header bubble and the
 * cart drawer read.
 * ====================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------- utils -- */

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /* Shopify's money_format templating. Only used for derived totals (the
   * "Add to basket — £48.00" label). Every price the customer reads as a
   * price is pre-formatted by Liquid and carried in the JSON island. */
  function formatMoney(cents, format) {
    var value = typeof cents === 'string' ? cents.replace(/[^0-9.\-]/g, '') : cents;
    var amount = Number(value) || 0;
    var template = format || '{{amount}}';

    function thousands(number, separator) {
      return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + separator);
    }

    function build(precision, thousandSep, decimalSep) {
      var negative = amount < 0;
      var whole = (Math.abs(amount) / 100).toFixed(precision);
      var parts = whole.split('.');
      var out = thousands(parts[0], thousandSep);
      if (parts[1]) out = out + decimalSep + parts[1];
      return (negative ? '-' : '') + out;
    }

    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    var placeholder = match ? match[1] : 'amount';
    var formatted;

    switch (placeholder) {
      case 'amount_no_decimals':
        formatted = build(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = build(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = build(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = build(2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = build(0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        formatted = build(2, ' ', ',');
        break;
      default:
        formatted = build(2, ',', '.');
    }

    /* money_format can carry markup; the caller inserts the result as text. */
    return template.replace(/\{\{\s*\w+\s*\}\}/, formatted).replace(/<[^>]*>/g, '');
  }

  /* ==================================================================== */
  /* PUBLIC FACADE                                                        */
  /* -------------------------------------------------------------------- */
  /* Declared before the elements are defined, because customElements      */
  /* .define() upgrades any matching element already in the document       */
  /* synchronously — connectedCallback must find this object waiting.      */
  /* ==================================================================== */

  var submitHooks = (window.IbcProduct && window.IbcProduct.submitHooks) || [];

  if (!window.IbcProduct) {
    window.IbcProduct = {
      instances: [],
      primary: null,
      submitHooks: submitHooks,

      /* WHAT THIS COPY OF THE FILE CAN DO, stated rather than inferred.
       *
       * Every asset in this theme is pasted in by hand, one file at a time,
       * and a package that lands half-installed has broken this shop before.
       * A caller that needs a behaviour this file only gained recently can
       * ask here and take a safe path when the answer is no, instead of
       * setting a flag an older copy would silently ignore. */
      capabilities: { skipParent: true },

      /* The last selection, whether or not an element is currently primary.
       * Late subscribers (ibc-addons.js) call this on init rather than
       * waiting for an `ibc:product-selection` event they may have missed. */
      lastSelection: (window.IbcProduct && window.IbcProduct.lastSelection) || null,

      getSelection: function () {
        if (window.IbcProduct.primary) return window.IbcProduct.primary.getSelection();
        return window.IbcProduct.lastSelection || null;
      },

      /* Re-dispatch the current selection from every live <ibc-product>.
       * Used on DOMContentLoaded and shopify:section:load so that anything
       * which attached after the initial synchronous dispatch is brought up
       * to date. */
      redispatchSelection: function () {
        var list = window.IbcProduct.instances.slice();
        for (var i = 0; i < list.length; i += 1) {
          if (typeof list[i]._emitSelection === 'function') list[i]._emitSelection();
        }
      },

      select: function (productId, variantId) {
        if (window.IbcProduct.primary) window.IbcProduct.primary.select(productId, variantId);
      },

      /* fn(ctx) may be async. Mutate ctx.properties and push to ctx.items.
       * Return { error: '…' } or throw to abort the add. */
      addSubmitHook: function (fn) {
        if (typeof fn === 'function' && submitHooks.indexOf(fn) === -1) submitHooks.push(fn);
        return fn;
      },

      removeSubmitHook: function (fn) {
        var i = submitHooks.indexOf(fn);
        if (i !== -1) submitHooks.splice(i, 1);
      },

      gallery: function () {
        return document.querySelector('ibc-media-gallery');
      }
    };
  }

  /* ==================================================================== */
  /* GALLERY                                                              */
  /* ==================================================================== */

  var IbcMediaGallery = function () {};

  if (typeof HTMLElement === 'function') {
    IbcMediaGallery = class extends HTMLElement {
      constructor() {
        super();
        this._index = 0;
        this._modalIndex = 0;
        this._onClick = this._onClick.bind(this);
        this._onKey = this._onKey.bind(this);
        this._onScroll = this._onScroll.bind(this);
        this._scrollTimer = null;
      }

      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.viewport = this.querySelector('[data-gallery-viewport]');
        this.track = this.querySelector('[data-gallery-track]');
        this.thumbList = this.querySelector('[data-gallery-thumbs]');
        this.live = this.querySelector('[data-gallery-live]');
        this.modal = document.getElementById(this.getAttribute('data-modal-id') || '');

        /* THE LIGHTBOX HAS TO LIVE ON <body>, NOT INSIDE THE GALLERY.
         *
         * `position: sticky` ALWAYS creates a stacking context -- unlike
         * `position: relative` with `z-index: auto`, which does not. When
         * `.pdp__media` became sticky so the photograph could hold while the
         * information column scrolls, this dialog -- a descendant of it --
         * was sealed inside that context. Its `z-index: 120` stopped meaning
         * "above the page" and started meaning "above its siblings inside
         * .pdp__media", and `.pdp__info`, which comes later in the document,
         * painted straight over the open lightbox: the option chips, the
         * price, everything, sitting on top of the photograph.
         *
         * No amount of z-index on the dialog can escape an ancestor's
         * stacking context. The element itself has to leave, and since it is
         * `position: fixed` it renders identically wherever it sits.
         *
         * SAFE BECAUSE EVERY REFERENCE IS BY ID. Both this component and
         * assets/ibc-dialog.js resolve it with `document.getElementById`,
         * never by walking down from the gallery, so moving it breaks no
         * lookup. Verified on the page: with the dialog on <body>, the
         * element painted at an option chip's centre is the lightbox image;
         * before it, it was the chip.
         *
         * STALE COPIES ARE REMOVED FIRST. A section re-render builds a fresh
         * dialog inside the gallery while the previous one is still parked
         * on <body>; two elements would then share an id and
         * `getElementById` would answer with whichever came first in the
         * document -- the orphan. Anything else holding this id goes before
         * the live one is moved. */
        if (this.modal && this.modal.parentElement !== document.body) {
          var modalId = this.modal.id;
          var self = this;
          if (modalId) {
            toArray(document.querySelectorAll('[id="' + modalId + '"]')).forEach(function (node) {
              if (node !== self.modal) node.parentNode && node.parentNode.removeChild(node);
            });
          }
          document.body.appendChild(this.modal);
        }

        this.addEventListener('click', this._onClick);
        if (this.viewport) this.viewport.addEventListener('keydown', this._onKey);
        if (this.track) this.track.addEventListener('scroll', this._onScroll, { passive: true });

        var first = this.visibleFrames()[0];
        if (first) this.goToMediaId(first.getAttribute('data-media-id'), { silent: true, scroll: false });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        if (this.viewport) this.viewport.removeEventListener('keydown', this._onKey);
        if (this.track) this.track.removeEventListener('scroll', this._onScroll);
        window.clearTimeout(this._scrollTimer);
        this._bound = false;
      }

      /* ------------------------------------------------------------ api -- */

      frames() {
        return toArray(this.querySelectorAll('[data-gallery-frame]'));
      }

      visibleFrames() {
        return this.frames().filter(function (frame) {
          return !frame.hidden;
        });
      }

      getMediaIds() {
        return this.visibleFrames().map(function (frame) {
          return frame.getAttribute('data-media-id');
        });
      }

      /* Show exactly these media, in this order. An empty or missing list
       * restores every frame — never leaves the gallery blank. */
      setMedia(mediaIds) {
        var wanted = (mediaIds || []).map(String).filter(Boolean);
        var frames = this.frames();
        var thumbs = toArray(this.querySelectorAll('[data-gallery-thumb-item]'));

        if (!wanted.length) {
          frames.forEach(function (frame) {
            frame.hidden = false;
          });
          thumbs.forEach(function (thumb) {
            thumb.hidden = false;
          });
        } else {
          frames.forEach(function (frame) {
            frame.hidden = wanted.indexOf(String(frame.getAttribute('data-media-id'))) === -1;
          });
          thumbs.forEach(function (thumb) {
            thumb.hidden = wanted.indexOf(String(thumb.getAttribute('data-media-id'))) === -1;
          });

          /* Re-order so the requested order is the display order. */
          if (this.track) {
            var trackRef = this.track;
            wanted.forEach(function (id) {
              var frame = trackRef.querySelector('[data-gallery-frame][data-media-id="' + id + '"]');
              if (frame) trackRef.appendChild(frame);
            });
          }
          if (this.thumbList) {
            var thumbRef = this.thumbList;
            wanted.forEach(function (id) {
              var item = thumbRef.querySelector('[data-gallery-thumb-item][data-media-id="' + id + '"]');
              if (item) thumbRef.appendChild(item);
            });
          }
        }

        var first = this.visibleFrames()[0];
        if (first) this.goToMediaId(first.getAttribute('data-media-id'), { silent: true });
      }

      goToMediaId(mediaId, options) {
        if (mediaId === null || mediaId === undefined) return;
        var target = String(mediaId);
        var index = -1;
        this.visibleFrames().forEach(function (frame, i) {
          if (String(frame.getAttribute('data-media-id')) === target) index = i;
        });
        if (index === -1) return;
        this.select(index, options);
      }

      select(index, options) {
        var opts = options || {};
        var frames = this.visibleFrames();
        if (!frames.length) return;
        var i = Math.max(0, Math.min(index, frames.length - 1));
        this._index = i;

        frames.forEach(function (frame, n) {
          frame.classList.toggle('is-active', n === i);
        });

        var activeId = String(frames[i].getAttribute('data-media-id'));
        toArray(this.querySelectorAll('[data-gallery-thumb]')).forEach(function (button) {
          var on = String(button.getAttribute('data-media-id')) === activeId;
          button.classList.toggle('is-on', on);
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        if (this.track && opts.scroll !== false) {
          var left = frames[i].offsetLeft - frames[0].offsetLeft;
          if (typeof this.track.scrollTo === 'function') {
            this.track.scrollTo({ left: left, behavior: reducedMotion() ? 'auto' : 'smooth' });
          } else {
            this.track.scrollLeft = left;
          }
        }

        if (this.live && !opts.silent) {
          this.live.textContent = 'Image ' + (i + 1) + ' of ' + frames.length;
        }
      }

      next() {
        this.select(this._index + 1);
      }

      prev() {
        this.select(this._index - 1);
      }

      /* --------------------------------------------------------- events -- */

      _onClick(event) {
        var thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb && this.contains(thumb)) {
          event.preventDefault();
          this.goToMediaId(thumb.getAttribute('data-media-id'));
          return;
        }
        if (event.target.closest('[data-gallery-next]')) {
          event.preventDefault();
          this.next();
          return;
        }
        if (event.target.closest('[data-gallery-prev]')) {
          event.preventDefault();
          this.prev();
          return;
        }
        var modalPrev = event.target.closest('[data-gallery-modal-prev]');
        var modalNext = event.target.closest('[data-gallery-modal-next]');
        if (modalPrev || modalNext) {
          event.preventDefault();
          this._stepModal(modalNext ? 1 : -1);
          return;
        }
        var zoom = event.target.closest('[data-gallery-zoom]');
        if (zoom && this.contains(zoom)) {
          /* ibc-dialog.js opens the dialog from its own delegated document
           * listener, which runs after this one, so the picture is already
           * in place by the time the dialog is shown. */
          this.goToMediaId(zoom.getAttribute('data-media-id'), { scroll: false });
          this._fillModal(this._index);
        }
      }

      _onKey(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prev();
        }
      }

      _onScroll() {
        var self = this;
        window.clearTimeout(this._scrollTimer);
        this._scrollTimer = window.setTimeout(function () {
          if (!self.track) return;
          var frames = self.visibleFrames();
          if (!frames.length) return;
          var left = self.track.scrollLeft;
          var best = 0;
          var bestDistance = Infinity;
          var origin = frames[0].offsetLeft;
          frames.forEach(function (frame, i) {
            var distance = Math.abs(frame.offsetLeft - origin - left);
            if (distance < bestDistance) {
              bestDistance = distance;
              best = i;
            }
          });
          if (best !== self._index) self.select(best, { scroll: false });
        }, 90);
      }

      /* ---------------------------------------------------------- modal -- */

      _imageFrames() {
        return this.visibleFrames().filter(function (frame) {
          return frame.getAttribute('data-media-type') === 'image';
        });
      }

      _fillModal(index) {
        if (!this.modal) return;
        var frames = this.visibleFrames();
        var frame = frames[index];
        if (!frame) return;
        var images = this._imageFrames();
        var position = images.indexOf(frame);
        if (position === -1) position = 0;
        this._modalIndex = position;
        this._paintModal();
      }

      _stepModal(direction) {
        var images = this._imageFrames();
        if (!images.length) return;
        this._modalIndex = (this._modalIndex + direction + images.length) % images.length;
        this._paintModal();
        this.goToMediaId(images[this._modalIndex].getAttribute('data-media-id'), { scroll: false });
      }

      _paintModal() {
        if (!this.modal) return;
        var images = this._imageFrames();
        var frame = images[this._modalIndex];
        if (!frame) return;
        var source = frame.querySelector('[data-gallery-image]');
        var target = this.modal.querySelector('[data-gallery-modal-image]');
        var caption = this.modal.querySelector('[data-gallery-modal-caption]');
        var nav = this.modal.querySelector('.ibc-gallery-modal__nav');
        if (source && target) {
          target.setAttribute('src', source.getAttribute('data-full-src') || source.currentSrc || source.src);
          target.setAttribute('alt', source.getAttribute('alt') || '');
        }
        if (caption) {
          caption.textContent = images.length > 1
            ? 'Image ' + (this._modalIndex + 1) + ' of ' + images.length
            : '';
        }
        if (nav) nav.hidden = images.length < 2;
      }
    };

    if (!window.customElements.get('ibc-media-gallery')) {
      window.customElements.define('ibc-media-gallery', IbcMediaGallery);
    }
  }

  /* ==================================================================== */
  /* PRODUCT                                                              */
  /* ==================================================================== */

  var IbcProductElement = function () {};

  if (typeof HTMLElement === 'function') {
    IbcProductElement = class extends HTMLElement {
      constructor() {
        super();
        this._onChange = this._onChange.bind(this);
        this._onClick = this._onClick.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._onQuantity = this._onQuantity.bind(this);
        this._onUnload = this._onUnload.bind(this);
        this.data = null;
        this.busy = false;
      }

      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        var island = this.querySelector('script[type="application/json"][data-ibc-product]');
        if (!island) return;
        try {
          this.data = JSON.parse(island.textContent);
        } catch (error) {
          this.data = null;
          return;
        }

        this.byId = {};
        var map = this.byId;
        (this.data.products || []).forEach(function (node) {
          map[String(node.id)] = node;
        });

        this.form = this.querySelector('[data-ibc-form]');
        this.idInput = this.querySelector('input[name="id"][data-ibc-variant-id]');
        this.gallery = this.querySelector('ibc-media-gallery');
        this.priceRegion = this.querySelector('[data-ibc-price]');
        this.availability = this.querySelector('[data-ibc-availability]');
        this.skuRow = this.querySelector('[data-ibc-sku]');
        this.skuValue = this.querySelector('[data-ibc-sku-value]');
        this.qtyInput = this.querySelector('[data-ibc-qty]');
        this.addButton = this.querySelector('[data-ibc-add]');
        this.addLabel = this.querySelector('[data-ibc-add-label]');

        /* THIS PRODUCT IS NOT THE THING BEING BOUGHT.
         *
         * Set by sections/main-product.liquid on a multipack: the pack
         * product is a page and a builder, and the PIECES are what reach the
         * basket, at their own variants and their own prices. Its own stock
         * and its own price therefore say nothing true about whether
         * anything can be bought here, and this file must stop reading them
         * — otherwise a pack product left at zero tracked stock renders a
         * disabled SOLD OUT button over a perfectly good builder, and the
         * first selection event rewrites the label to
         * "Add to basket — £0.00" from a price that is never charged.
         *
         * The wording the merchant set is kept as it was written, so the
         * label simply stays put. */
        this.notForSale = this.hasAttribute('data-ibc-not-for-sale');
        this.baseAddLabel = this.addLabel ? this.addLabel.textContent : '';
        this.errorRegion = this.querySelector('[data-ibc-error]');
        this.statusRegion = this.querySelector('[data-ibc-status]');
        this.addOnsRegion = this.querySelector('[data-ibc-addons-region]');

        this.addEventListener('change', this._onChange);
        this.addEventListener('click', this._onClick);
        if (this.form) this.form.addEventListener('submit', this._onSubmit);
        if (this.qtyInput) this.qtyInput.addEventListener('input', this._onQuantity);
        document.addEventListener('shopify:section:unload', this._onUnload);

        /* Accordions are served OPEN so the content is readable without
         * JavaScript. Collapsing them is the enhancement.
         *
         * `data-ibc-acc-open` opts a row OUT of that collapse: it is served
         * open and stays open until somebody presses it. EVERY row of the
         * Information section carries it -- About, Allergens, Size and
         * Delivery -- so that block arrives fully readable: the rows are
         * short, there are at most four, and an allergen statement that has
         * to be found and pressed is an allergen statement some people will
         * not read. Every accordion that existed before this attribute
         * carries no such marker and behaves exactly as it did. */
        toArray(this.querySelectorAll('[data-ibc-acc-toggle]')).forEach(function (toggle) {
          if (toggle.hasAttribute('data-ibc-acc-open')) return;
          var panel = document.getElementById(toggle.getAttribute('aria-controls'));
          toggle.setAttribute('aria-expanded', 'false');
          if (panel) panel.hidden = true;
        });

        /* With JS on, the <noscript> select is not parsed, so the hidden
         * input is the only thing named "id" and is authoritative. */
        var restored = this._fromUrl();
        this.select(restored.productId, restored.variantId, { replaceUrl: false, silent: false });

        if (!window.IbcProduct.primary) window.IbcProduct.primary = this;
        if (window.IbcProduct.instances.indexOf(this) === -1) {
          window.IbcProduct.instances.push(this);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onChange);
        this.removeEventListener('click', this._onClick);
        if (this.form) this.form.removeEventListener('submit', this._onSubmit);
        if (this.qtyInput) this.qtyInput.removeEventListener('input', this._onQuantity);
        document.removeEventListener('shopify:section:unload', this._onUnload);
        var i = window.IbcProduct.instances.indexOf(this);
        if (i !== -1) window.IbcProduct.instances.splice(i, 1);
        if (window.IbcProduct.primary === this) {
          window.IbcProduct.primary = window.IbcProduct.instances[0] || null;
        }
        this._bound = false;
      }

      _onUnload(event) {
        if (event.target && event.target.contains && event.target.contains(this)) {
          this.disconnectedCallback();
        }
      }

      /* ---------------------------------------------------------- state -- */

      product(productId) {
        return this.byId[String(productId)] || null;
      }

      variant(productId, variantId) {
        var node = this.product(productId);
        if (!node) return null;
        var found = null;
        (node.variants || []).forEach(function (v) {
          if (String(v.id) === String(variantId)) found = v;
        });
        return found;
      }

      getSelection() {
        var node = this.product(this.selectedProductId);
        return {
          productId: this.selectedProductId,
          variantId: this.selectedVariantId,
          product: node,
          variant: this.variant(this.selectedProductId, this.selectedVariantId)
        };
      }

      _fromUrl() {
        var fallback = {
          productId: this.data.productId,
          variantId: this.data.variantId
        };
        if (!window.URLSearchParams) return fallback;
        var params = new URLSearchParams(window.location.search);

        /* The live theme wrote ?linked_product= and never read it back, so
         * deep links and refreshes silently lost the customer's choice.
         * Reading it here is the fix; the parameter contract is unchanged. */
        var handle = params.get('linked_product');
        if (handle) {
          var match = null;
          (this.data.products || []).forEach(function (node) {
            if (node.handle === handle) match = node;
          });
          if (match) {
            return { productId: match.id, variantId: match.selectedVariantId };
          }
        }

        var variantId = params.get('variant');
        if (variantId && this.variant(this.data.productId, variantId)) {
          return { productId: this.data.productId, variantId: Number(variantId) };
        }
        return fallback;
      }

      /* --------------------------------------------------------- select -- */

      select(productId, variantId, options) {
        var opts = options || {};
        var node = this.product(productId) || this.product(this.data.productId);
        if (!node) return;

        var variant = this.variant(node.id, variantId);
        if (!variant) {
          variant = this.variant(node.id, node.selectedVariantId) || (node.variants || [])[0];
        }
        if (!variant) return;

        this.selectedProductId = node.id;
        this.selectedVariantId = variant.id;

        this._syncPicker(node, variant);
        this._syncForm(node, variant);
        this._syncPrice(variant);
        this._syncAvailability(node, variant);
        this._syncSku(variant);
        this._syncMedia(node, variant);
        this._syncAllergens(node, variant);
        this._syncVegan(node);
        this._syncPickupNote(node);
        this._syncQuantity(variant);
        this._syncAddOnsRegion(node);
        this._syncButton(node, variant);

        if (opts.replaceUrl !== false) this._syncUrl(node, variant);

        /* Retain the selection so a listener that attaches LATER can still
         * catch up. `ibc-addons.js` is deferred after this file, so it is
         * guaranteed to miss this very first event; it reads
         * `window.IbcProduct.getSelection()` on init instead of waiting for
         * the next one. See QA D-02. */
        this._lastSelection = {
          productId: node.id,
          variantId: variant.id,
          product: node,
          variant: variant,
          element: this
        };
        window.IbcProduct.lastSelection = this._lastSelection;

        this._emitSelection();
      }

      /* Dispatch the CURRENT selection again. Safe to call any number of
       * times: it carries no side effect beyond the event itself. */
      _emitSelection() {
        var detail = this._lastSelection;
        if (!detail) return;
        this.dispatchEvent(
          new CustomEvent('ibc:product-selection', {
            bubbles: true,
            composed: true,
            detail: {
              productId: detail.productId,
              variantId: detail.variantId,
              product: detail.product,
              variant: detail.variant,
              element: this
            }
          })
        );
      }

      /* The quantity steppers and the description accordions. Both are real
       * buttons, so keyboard operation needs nothing extra. */
      _onClick(event) {
        /* QA D-03. "Remove" unticks the personalisation toggle in code, so no
         * `change` event follows it — re-price the button here instead.
         * ibc-addons.js listens on [data-ibc-addons-root], which is inside
         * this element, so it has already handled the click by now. */
        if (event.target.closest && event.target.closest('[data-ibc-pers-remove]')) {
          this._onQuantity();
          return;
        }

        var step = event.target.closest('[data-ibc-qty-step]');
        if (step && this.qtyInput) {
          event.preventDefault();
          var direction = step.getAttribute('data-ibc-qty-step') === 'down' ? -1 : 1;
          var max = parseInt(this.qtyInput.getAttribute('max'), 10);
          var next = this._quantity() + direction;
          if (next < 1) next = 1;
          if (!isNaN(max) && next > max) next = max;
          this.qtyInput.value = String(next);
          this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        var toggle = event.target.closest('[data-ibc-acc-toggle]');
        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          var panel = document.getElementById(toggle.getAttribute('aria-controls'));
          if (!panel) return;
          var open = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
        }
      }

      _onChange(event) {
        var target = event.target;
        if (!target || target.tagName !== 'INPUT') return;

        /* QA D-03. `assets/ibc-addons.js` listens on [data-ibc-addons-root],
         * which is inside this element, so its own state is already applied
         * by the time this bubbles up here. Re-price the button, nothing
         * else. */
        if (
          target.hasAttribute('data-ibc-addon-toggle') ||
          target.hasAttribute('data-ibc-pers-toggle')
        ) {
          this._onQuantity();
          return;
        }

        if (target.hasAttribute('data-ibc-linked-option')) {
          this.select(target.getAttribute('data-product-id'), target.getAttribute('data-variant-id'));
          return;
        }

        if (target.hasAttribute('data-ibc-linked-variant')) {
          this.select(target.getAttribute('data-product-id'), target.getAttribute('data-variant-id'));
          return;
        }

        if (target.hasAttribute('data-ibc-native-option')) {
          var node = this.product(this.data.productId);
          if (!node) return;
          var chosen = [];
          toArray(this.querySelectorAll('[data-ibc-native-options] input[data-ibc-native-option]:checked'))
            .forEach(function (input) {
              chosen[Number(input.getAttribute('data-option-position')) - 1] =
                input.getAttribute('data-option-value');
            });

          var match = null;
          (node.variants || []).forEach(function (v) {
            if (match) return;
            var same = true;
            chosen.forEach(function (value, i) {
              if (value !== undefined && v.options[i] !== value) same = false;
            });
            if (same) match = v;
          });

          if (match) {
            this.select(node.id, match.id);
          } else {
            /* No such combination exists. Say so instead of leaving a
             * selected-looking button with a stale price, which is what the
             * live theme did. */
            this._setError('That combination is not available. Please choose another.');
            this._syncNativeAvailability(node, chosen);
          }
        }
      }

      /* ------------------------------------------------------------ ui -- */

      _syncPicker(node, variant) {
        var isCurrent = String(node.id) === String(this.data.productId);

        toArray(this.querySelectorAll('input[data-ibc-linked-option]')).forEach(function (input) {
          var on = String(input.getAttribute('data-product-id')) === String(node.id);
          input.checked = on;
          if (input.parentElement) input.parentElement.classList.toggle('is-on', on);
        });

        var native = this.querySelector('[data-ibc-native-options]');
        if (native) native.hidden = !isCurrent;

        toArray(this.querySelectorAll('[data-ibc-linked-variants]')).forEach(function (group) {
          group.hidden = String(group.getAttribute('data-product-id')) !== String(node.id);
        });

        if (isCurrent) {
          toArray(this.querySelectorAll('[data-ibc-native-options] input[data-ibc-native-option]'))
            .forEach(function (input) {
              var position = Number(input.getAttribute('data-option-position')) - 1;
              var on = variant.options[position] === input.getAttribute('data-option-value');
              input.checked = on;
              if (input.parentElement) input.parentElement.classList.toggle('is-on', on);
            });
          this._syncNativeAvailability(node, variant.options);
        }
      }

      /* Mark combinations that do not resolve to a purchasable variant,
       * taking the OTHER selected options into account — the live theme
       * only ever looked at one option index at a time. */
      _syncNativeAvailability(node, chosen) {
        var variants = node.variants || [];
        toArray(this.querySelectorAll('[data-ibc-native-options] input[data-ibc-native-option]'))
          .forEach(function (input) {
            var position = Number(input.getAttribute('data-option-position')) - 1;
            var value = input.getAttribute('data-option-value');
            var candidate = chosen.slice();
            candidate[position] = value;

            var available = false;
            variants.forEach(function (v) {
              if (available) return;
              var same = true;
              candidate.forEach(function (want, i) {
                if (want !== undefined && v.options[i] !== want) same = false;
              });
              if (same && v.available) available = true;
            });

            if (available) {
              input.removeAttribute('data-unavailable');
            } else {
              input.setAttribute('data-unavailable', 'true');
            }
            if (input.parentElement) {
              input.parentElement.classList.toggle('is-unavailable', !available);
              var state = input.parentElement.querySelector('[data-ibc-option-state]');
              if (state) state.textContent = available ? '' : ' — unavailable';
            }
          });
      }

      _syncForm(node, variant) {
        if (this.idInput) this.idInput.value = variant.id;
        if (this.form) {
          /* Always the native cart route; the variant id decides the
           * product, so the action never needs a product handle in it. */
          this.form.setAttribute('action', root() + 'cart/add');
          this.form.setAttribute('data-selected-product-id', String(node.id));
        }
        this.setAttribute('data-selected-product-id', String(node.id));
        this.setAttribute('data-selected-variant-id', String(variant.id));
      }

      _syncPrice(variant) {
        if (!this.priceRegion) return;
        this.priceRegion.textContent = '';

        var wrap = document.createElement('span');
        wrap.className = 'ibc-price' + (variant.onSale ? ' ibc-price--on-sale' : '') +
          (variant.available ? '' : ' ibc-price--sold-out');

        var amount = document.createElement('span');
        amount.className = 'ibc-price__amount';
        if (variant.onSale) {
          var saleLabel = document.createElement('span');
          saleLabel.className = 'visually-hidden';
          saleLabel.textContent = 'Sale price ';
          amount.appendChild(saleLabel);
        }
        amount.appendChild(document.createTextNode(variant.priceFormatted || ''));
        wrap.appendChild(amount);

        if (variant.onSale && variant.compareAtFormatted) {
          var compare = document.createElement('s');
          compare.className = 'ibc-price__compare';
          var wasLabel = document.createElement('span');
          wasLabel.className = 'visually-hidden';
          wasLabel.textContent = 'Regular price ';
          compare.appendChild(wasLabel);
          compare.appendChild(document.createTextNode(variant.compareAtFormatted));
          wrap.appendChild(compare);
        }

        this.priceRegion.appendChild(wrap);
      }

      _syncAvailability(node, variant) {
        if (!this.availability) return;
        var text = '';
        if (!variant.available) {
          text = 'Sold out';
        } else if (typeof variant.quantityAvailable === 'number' && variant.quantityAvailable > 0 && variant.quantityAvailable <= 5) {
          text = 'Only ' + variant.quantityAvailable + ' left';
        } else {
          text = 'In stock';
        }
        this.availability.textContent = text;
        this.availability.className = 'ibc-pdp__stock' + (variant.available ? ' is-in' : ' is-out');
      }

      _syncSku(variant) {
        if (!this.skuRow) return;
        var sku = variant.sku || '';
        this.skuRow.hidden = sku === '';
        if (this.skuValue) this.skuValue.textContent = sku;
      }

      _syncMedia(node, variant) {
        if (!this.gallery || typeof this.gallery.setMedia !== 'function') return;
        this.gallery.setMedia(node.mediaIds || []);
        var mediaId = variant.featuredMediaId || node.featuredMediaId;
        if (mediaId) this.gallery.goToMediaId(mediaId, { silent: true });
      }

      /* VARIANT FIRST, PRODUCT SECOND, PER STATEMENT.
       *
       * `contains` and `traces` fall back INDEPENDENTLY: a variant that
       * declares its own allergens but no traces overrides the first and
       * inherits the second. An empty variant list means "inherit", never
       * "this variant has no allergens" — there is no way to say the latter
       * at variant level and the food-labelling reading of a blank cell is
       * always the safer one.
       *
       * This is the same rule `snippets/ibc-allergens.liquid` applies to the
       * server-rendered first paint. The two MUST agree or the panel flickers
       * to different text on hydration. Both read the same two raw lists off
       * the island: `variant.allergens` / `variant.traceAllergens` are the
       * variant's own, `node.allergens` / `node.traceAllergens` the
       * product's. Nothing is pre-merged upstream.
       *
       * `variant` is optional so an older caller that passes only the node
       * still gets the product statement rather than throwing. */
      /* THE VEGAN MARK ON THE TITLE.
       *
       * A linked-product chip changes the PRODUCT — Whole Cake to Slice —
       * and those are two different products with two different sets of
       * variants, so the title's mark has to change with it. Vegan Cake and
       * Vegan Cake Slice are exactly the pair this exists for.
       *
       * `node.vegan` is the state the server computed for that product:
       * 'all', 'some' or ''. Only 'all' earns a mark on the title, because a
       * title cannot be qualified by which option happens to be selected —
       * when it is 'some' the option chips carry the mark instead, and those
       * are server-rendered per product and never touched here.
       *
       * The slot is emitted by sections/main-product.liquid whether or not
       * it applies, so this only ever shows and hides what is already there.
       * Nothing is built from strings: the badge markup lives in
       * snippets/ibc-vegan-badge.liquid and is cloned from the slot's own
       * first paint, so the two can never drift apart. If the page opened on
       * a product that is not wholly vegan there is nothing to clone, and
       * the mark simply never appears — which is the safe way round for a
       * regulated claim: this file can hide a mark, and can put back one the
       * server drew, but it can never invent one. */
      _syncVegan(node) {
        var slot = this.querySelector('[data-ibc-vegan-slot]');
        if (!slot) return;

        if (!this._veganMark) {
          var painted = slot.querySelector('[data-ibc-vegan-mark]');
          if (painted) this._veganMark = painted.cloneNode(true);
        }

        var wanted = node && node.vegan === 'all';
        if (!wanted) {
          slot.textContent = '';
          slot.hidden = true;
          return;
        }
        if (!this._veganMark) return;
        if (!slot.querySelector('[data-ibc-vegan-mark]')) {
          slot.appendChild(this._veganMark.cloneNode(true));
        }
        slot.hidden = false;
      }

      _syncAllergens(node, variant) {
        var region = this.querySelector('[data-ibc-allergens]');
        if (!region) return;
        var body = region.querySelector('[data-ibc-allergens-body]');
        if (!body) return;

        var scope = variant || {};
        var allergens =
          scope.allergens && scope.allergens.length ? scope.allergens : node.allergens || [];
        var traces =
          scope.traceAllergens && scope.traceAllergens.length
            ? scope.traceAllergens
            : node.traceAllergens || [];
        body.textContent = '';

        if (!allergens.length && !traces.length) {
          region.hidden = true;
          region.setAttribute('data-product-id', String(node.id));
          if (scope.id) region.setAttribute('data-variant-id', String(scope.id));
          return;
        }
        region.hidden = false;
        region.setAttribute('data-product-id', String(node.id));
        if (scope.id) region.setAttribute('data-variant-id', String(scope.id));

        /* The server wrote `id`s on the heading and the trace label and
         * pointed `aria-labelledby` at them. Rebuilding without them left
         * both lists labelled by an id that no longer existed — harmless
         * while this ran only on a linked-product switch, less so now it
         * runs on every option change. The prefix comes off the region so
         * the rebuilt ids are byte-for-byte the ones the server wrote. */
        var prefix = region.getAttribute('data-ibc-allergens-prefix') || '';
        var headingId = prefix ? prefix + '-allergens-heading' : '';
        var traceLabelId = prefix ? prefix + '-trace-label' : '';

        var heading = document.createElement('h2');
        heading.className = 'ibc-allergens__heading';
        heading.textContent = 'Allergens';
        if (headingId) heading.id = headingId;
        body.appendChild(heading);

        if (allergens.length) {
          var list = document.createElement('ul');
          list.className = 'ibc-allergens__list';
          list.setAttribute('role', 'list');
          if (headingId) list.setAttribute('aria-labelledby', headingId);
          allergens.forEach(function (item) {
            var li = document.createElement('li');
            li.className = 'ibc-allergens__pill';
            li.textContent = item;
            list.appendChild(li);
          });
          body.appendChild(list);
        }

        if (traces.length) {
          var traceWrap = document.createElement('div');
          traceWrap.className = 'ibc-allergens__trace';
          var label = document.createElement('p');
          label.className = 'ibc-allergens__trace-label';
          /* Exact wording — a food-labelling contract, not copy. */
          label.textContent = 'May contain traces of:';
          if (traceLabelId) label.id = traceLabelId;
          traceWrap.appendChild(label);
          var traceList = document.createElement('ul');
          traceList.className = 'ibc-allergens__trace-list';
          traceList.setAttribute('role', 'list');
          if (traceLabelId) traceList.setAttribute('aria-labelledby', traceLabelId);
          traces.forEach(function (item) {
            var li = document.createElement('li');
            li.className = 'ibc-allergens__trace-item';
            li.textContent = item;
            traceList.appendChild(li);
          });
          traceWrap.appendChild(traceList);
          body.appendChild(traceWrap);
        }

        var note = document.createElement('p');
        note.className = 'ibc-allergens__note';
        note.textContent = 'Please speak to our team before ordering if you have an allergy or intolerance.';
        body.appendChild(note);
      }

      _syncPickupNote(node) {
        toArray(this.querySelectorAll('[data-ibc-pickup-note]')).forEach(function (element) {
          element.hidden = String(element.getAttribute('data-product-id')) !== String(node.id);
        });
      }

      _syncQuantity(variant) {
        if (!this.qtyInput) return;
        var max = typeof variant.quantityAvailable === 'number' ? variant.quantityAvailable : null;
        if (max !== null && max > 0) {
          this.qtyInput.setAttribute('max', String(max));
          if (Number(this.qtyInput.value) > max) this.qtyInput.value = String(max);
        } else {
          this.qtyInput.removeAttribute('max');
        }
        if (Number(this.qtyInput.value) < 1 || isNaN(Number(this.qtyInput.value))) {
          this.qtyInput.value = '1';
        }
      }

      _syncAddOnsRegion(node) {
        if (this.addOnsRegion) {
          this.addOnsRegion.setAttribute('data-selected-product-id', String(node.id));
        }
        /* Pre-rendered per-product add-on panels, if the add-on agent uses
         * that pattern. Harmless when there are none. */
        var panels = toArray(this.querySelectorAll('[data-addons-for-product]'));
        if (!panels.length) return;
        panels.forEach(function (panel) {
          panel.hidden = String(panel.getAttribute('data-addons-for-product')) !== String(node.id);
        });
      }

      _syncButton(node, variant) {
        if (!this.addButton) return;
        var soldOut = !variant.available && !this.notForSale;
        this.addButton.disabled = soldOut || this.busy;
        this.addButton.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
        if (!this.addLabel) return;
        if (soldOut) {
          this.addLabel.textContent = 'Sold out';
          return;
        }
        /* No running total on the button here: the price of what is being
         * built is not this product's price, and the builder writes the real
         * one into the page's price slot. */
        if (this.notForSale) {
          this.addLabel.textContent = this.baseAddLabel;
          return;
        }
        var quantity = this._quantity();
        var total = ((variant.price || 0) + this._extrasPerUnit(node)) * quantity;
        this.addLabel.textContent = 'Add to basket — ' + formatMoney(total, this.data.moneyFormat);
      }

      /* QA D-03: the button quoted the parent variant alone while the POST
       * sent the add-on and personalisation lines too, so a ticked add-on
       * read "£48.00" and charged £65.00.
       *
       * This READS the same controls `assets/ibc-addons.js` reads when it
       * builds those lines — the visible panel for the SELECTED product,
       * each available add-on whose toggle is ticked, and the
       * personalisation block when it is on show and ticked — and adds their
       * `data-price` (minor units, written by the same Liquid that prices
       * the line). It writes nothing and touches no line-item property; the
       * add-on files stay the single writer of the cart payload. Each child
       * line goes in at the parent's quantity, so the extras are per unit. */
      _extrasPerUnit(node) {
        var addOnsRoot = this.querySelector('[data-ibc-addons-root]');
        if (!addOnsRoot || !node) return 0;

        var wanted = String(node.id);
        var extra = 0;

        toArray(addOnsRoot.querySelectorAll('[data-addons-for-product]')).forEach(function (panel) {
          if (String(panel.getAttribute('data-addons-for-product')) !== wanted) return;
          toArray(panel.querySelectorAll('[data-ibc-addon]')).forEach(function (addon) {
            if (addon.getAttribute('data-available') !== 'true') return;
            var toggle = addon.querySelector('input[data-ibc-addon-toggle]');
            if (!toggle || !toggle.checked) return;
            var price = parseInt(addon.getAttribute('data-price'), 10);
            if (!isNaN(price)) extra += price;
          });
        });

        var pers = addOnsRoot.querySelector('[data-ibc-personalisation]');
        if (pers && !pers.hidden) {
          var persToggle = pers.querySelector('[data-ibc-pers-toggle]');
          if (persToggle && persToggle.checked) {
            var persPrice = parseInt(pers.getAttribute('data-price'), 10);
            if (!isNaN(persPrice)) extra += persPrice;
          }
        }

        return extra;
      }

      _syncUrl(node, variant) {
        if (!window.history || !window.history.replaceState || !window.URL) return;
        var url = new URL(window.location.href);
        /* The path never changes. The linked products are `hide`-tagged and
         * their own pages are noindexed and have no picker, so sending the
         * customer there on refresh would lose the option group. Same
         * parameter contract as the live theme, now actually read back. */
        if (String(node.id) === String(this.data.productId)) {
          url.searchParams.delete('linked_product');
          url.searchParams.set('variant', String(variant.id));
        } else {
          url.searchParams.delete('variant');
          url.searchParams.set('linked_product', node.handle);
        }
        window.history.replaceState({}, '', url.toString());
      }

      _onQuantity() {
        var selection = this.getSelection();
        if (selection.variant) this._syncButton(selection.product, selection.variant);
      }

      _quantity() {
        if (!this.qtyInput) return 1;
        var value = parseInt(this.qtyInput.value, 10);
        if (isNaN(value) || value < 1) value = 1;
        return value;
      }

      /* --------------------------------------------------------- errors -- */

      _setError(message) {
        if (!this.errorRegion) return;
        this.errorRegion.textContent = message || '';
        this.errorRegion.hidden = !message;
      }

      _setStatus(message) {
        if (this.statusRegion) this.statusRegion.textContent = message || '';
      }

      _setBusy(busy) {
        this.busy = busy;
        if (!this.addButton) return;
        this.addButton.classList.toggle('is-busy', busy);
        this.addButton.setAttribute('aria-busy', busy ? 'true' : 'false');
        var selection = this.getSelection();
        var buyable = this.notForSale || !!(selection.variant && selection.variant.available);
        this.addButton.disabled = busy || !buyable;
      }

      /* ---------------------------------------------------- add to cart -- */

      _onSubmit(event) {
        event.preventDefault();
        this.addToCart();
      }

      addToCart() {
        var self = this;
        if (this.busy) return Promise.resolve();
        var selection = this.getSelection();
        if (!selection.variant) return Promise.resolve();

        this._setError('');
        this._setBusy(true);
        this._setStatus('Adding to your basket…');

        var context = {
          selection: selection,
          product: selection.product,
          variant: selection.variant,
          quantity: this._quantity(),
          properties: {},
          items: [],
          element: this
        };

        var chain = Promise.resolve();
        submitHooks.forEach(function (hook) {
          chain = chain.then(function () {
            return hook(context);
          }).then(function (result) {
            if (result && result.error) throw new Error(result.error);
          });
        });

        return chain
          .then(function () {
            var parent = { id: Number(context.selection.variantId), quantity: context.quantity };
            if (Object.keys(context.properties).length) parent.properties = context.properties;

            /* THE PARENT IS THE DEFAULT, NOT THE RULE. A hook that has set
             * `skipParent` is saying this product is not the thing being
             * bought — the multipack, whose pieces it has just pushed onto
             * `context.items` at their own prices. Adding the pack product
             * as well would charge for a page.
             *
             * An empty POST is refused here rather than sent: /cart/add.js
             * answers `{ items: [] }` cheerfully, which would look to every
             * later step exactly like a successful add of nothing. */
            var items = context.skipParent === true ? context.items.slice() : [parent].concat(context.items);
            if (!items.length) {
              throw new Error('Nothing was chosen to add to your basket.');
            }

            return fetch(root() + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: items })
            }).then(function (response) {
              return response.json().then(function (data) {
                if (!response.ok) {
                  throw new Error(
                    data.description || data.message || 'Sorry, that could not be added to your basket.'
                  );
                }
                return data;
              });
            });
          })
          .then(function () {
            return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
              .then(function (response) {
                return response.json();
              })
              .catch(function () {
                return null;
              });
          })
          .then(function (cart) {
            self._setStatus('Added to your basket.');
            if (self.addButton) {
              self.addButton.classList.add('is-done');
              window.setTimeout(function () {
                if (self.addButton) self.addButton.classList.remove('is-done');
              }, 1800);
            }
            document.dispatchEvent(
              new CustomEvent('ibc:cart-changed', {
                detail: {
                  cart: cart,
                  item_count: cart ? cart.item_count : undefined,
                  items: cart ? cart.items : undefined,
                  total_price: cart ? cart.total_price : undefined,
                  source: 'product'
                }
              })
            );
          })
          .catch(function (error) {
            self._setStatus('');
            self._setError((error && error.message) || 'Sorry, that could not be added to your basket.');
            /* QA D-13: move focus to whatever blocked the add, when a hook
             * has told us what that was. Otherwise leave focus where it is —
             * the message carries `role="alert"` and is announced anyway. */
            var target = error && error.focusTarget;
            if (target && typeof target.focus === 'function') {
              try {
                target.focus();
              } catch (ignore) {
                /* a control inside a [hidden] ancestor cannot take focus */
              }
            }
          })
          .then(function () {
            self._setBusy(false);
          });
      }
    };

    if (!window.customElements.get('ibc-product')) {
      window.customElements.define('ibc-product', IbcProductElement);
    }
  }

  /* --------------------------------------------------------- catch-up -- */
  /* Deferred scripts run in document order, so `connectedCallback` fires the
   * first `ibc:product-selection` before later modules have attached their
   * listeners. Re-announce it once the document is parsed, and again after a
   * theme-editor section reload. Listeners are idempotent. */
  function redispatch() {
    if (window.IbcProduct && typeof window.IbcProduct.redispatchSelection === 'function') {
      window.IbcProduct.redispatchSelection();
    }
  }

  /* Deferred scripts all run while readyState is "interactive", so waiting
   * for DOMContentLoaded genuinely puts this after ibc-addons.js. Only a
   * script injected into an already-loaded document replays immediately. */
  if (document.readyState === 'complete') {
    redispatch();
  } else {
    document.addEventListener('DOMContentLoaded', redispatch);
  }
  document.addEventListener('shopify:section:load', redispatch);

  /* ------------------------------------------------------ back button -- */
  /* `[data-ibc-back]` is served by sections/main-product.liquid as a REAL
   * link to a real collection, so it works with no JavaScript and can be
   * opened in a new tab. All this does is upgrade the left-click to
   * `history.back()` — which is the only thing that can restore the
   * customer's scroll position in the grid they came from — and only when
   * going back is genuinely going to land them on a listing.
   *
   * The test, in full:
   *   * there is a referrer, and it parses;
   *   * it is SAME-ORIGIN (a link in from Google or Instagram must not send
   *     the customer back out of the shop);
   *   * its path is a collection or a search page, and NOT a product page
   *     inside a collection (`/collections/x/products/y` is a product page);
   *   * the session has somewhere to go back to.
   *
   * Anything else and the served href stands, untouched.
   *
   * The accessible name follows the behaviour: it keeps the served
   * destination when that IS where back goes, and becomes generic when it
   * is not, so the control never names a place it will not take you. */
  function backSource() {
    if (!document.referrer) return null;
    if (!window.URL) return null;
    if (!window.history || typeof window.history.back !== 'function') return null;
    if (window.history.length <= 1) return null;

    var from;
    try {
      from = new URL(document.referrer, window.location.href);
    } catch (error) {
      return null;
    }
    if (from.origin !== window.location.origin) return null;

    var path = from.pathname || '';
    if (path.indexOf('/products/') !== -1) return null;
    if (!/(^|\/)collections(\/|$)/.test(path) && !/(^|\/)search(\/|$)/.test(path)) return null;
    return from;
  }

  function initBack(scope) {
    toArray((scope || document).querySelectorAll('[data-ibc-back]')).forEach(function (link) {
      if (link._ibcBackBound) return;
      link._ibcBackBound = true;

      var from = backSource();
      if (!from) return;

      var name = link.querySelector('[data-ibc-back-name]');
      if (name) {
        var served = link.getAttribute('data-ibc-back-fallback') || '';
        var servedPath = served;
        try {
          servedPath = new URL(served, window.location.href).pathname;
        } catch (error) {
          servedPath = served;
        }
        if (servedPath !== from.pathname) {
          name.textContent = 'Back to the page you came from';
        }
      }

      link.addEventListener('click', function (event) {
        if (event.defaultPrevented) return;
        /* Leave every "open somewhere else" gesture to the browser. */
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.history.back();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initBack(document);
    });
  } else {
    initBack(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    initBack(event.target || document);
  });

  window.IbcMediaGallery = IbcMediaGallery;
})();

/* =========================================================================
   DELIVERY PROMISE — THE SAME-DAY ROW, AGAINST THE CLOCK
   =========================================================================
   snippets/ibc-delivery-promise.liquid writes the same-day row in its RULE
   form: "Order by 7pm", which is true at every hour of the day. That is the
   only thing Liquid can honestly say, because `now` there is the moment the
   section was rendered and a cached section will cheerfully promise a 7pm
   courier at nine in the evening. The basket's two cut-offs already make
   exactly this trade; this is the same rule applied one page earlier.

   So the browser — which does have a trustworthy clock — upgrades the row
   once, on load. Past the cut-off the note is replaced with the sentence the
   snippet parked in `data-past-note` and the row is dimmed. It is dimmed
   rather than removed: somebody who came looking for same-day should learn
   that today's has gone, not be left wondering whether the shop does it.

   WITH JAVASCRIPT OFF none of this runs and the row still reads correctly.
   That is the point of writing it in the rule form to begin with.

   NO TIMER. The window between a customer loading a product page and pressing
   Add to basket is minutes, and a page that silently rewrites itself while
   being read is worse than one that is a few minutes stale. It re-runs on
   `shopify:section:load` because the theme editor rebuilds sections in place.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  /* "19:00" -> 1140. Anything unparseable -> null, and the caller leaves the
     row exactly as Liquid wrote it. A broken setting must not be able to
     withdraw an offer the shop is still making. */
  function minutesOf(value) {
    if (!value) return null;
    var parts = String(value).split(':');
    if (parts.length < 2) return null;
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
  }

  /* Swap an element's contents for `replacement` while `past`, and put back
     what Liquid wrote when it is not.

     IT MUST NOT TOUCH THE ELEMENT IN THE ORDINARY CASE. An earlier version
     assigned `textContent` on every run, `past` or not, and so quietly
     deleted the `<b>` around every time of day on every page load before the
     cut-off — the overwhelmingly common one. The page looked right in the
     markup and wrong in the browser, which is the only place it is read.
     Nothing but a screenshot was ever going to catch it.

     So: `data-swapped` records whether this element is currently showing the
     replacement, and each branch returns immediately when there is nothing
     to do. The restore writes back `innerHTML` because the original has
     markup in it — and the only string ever written is one this function
     captured off this same element moments earlier, never anything from a
     customer, a URL or an API. */
  function swap(el, replacement, past) {
    if (!el || !replacement) return;

    if (past) {
      if (el.hasAttribute('data-swapped')) return;
      el.setAttribute('data-original-html', el.innerHTML);
      el.setAttribute('data-swapped', '');
      el.textContent = replacement;
      return;
    }

    if (!el.hasAttribute('data-swapped')) return;
    el.innerHTML = el.getAttribute('data-original-html') || '';
    el.removeAttribute('data-original-html');
    el.removeAttribute('data-swapped');
  }

  /* ------------------------------------------------------------------
   * THE QUICKEST DELIVERY'S ESTIMATED DATE, for one product.
   *
   * The same rules, in the same order, as dayState() in
   * assets/ibc-fulfilment.js, which decides what the basket's calendar
   * offers -- so the date promised here is the date the basket will show.
   * Change one, change the other.
   *
   *   earliest  today + lead days, +1 once the cut-off has passed
   *   skip      a blackout date, a closed weekday (perishable days included
   *             for a fresh product), and a Monday that is either shut to
   *             fresh food or inside the Monday notice
   *
   * LONDON TIME, NOT THE VISITOR'S. The cut-off is the shop's 11am, so a
   * visitor abroad must not be told tomorrow at their 10am.
   * ------------------------------------------------------------------ */
  var EST_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var EST_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

  function londonNow() {
    try {
      var parts = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
      return {
        today: new Date(+parts.year, +parts.month - 1, +parts.day, 12),
        mins: (+parts.hour % 24) * 60 + (+parts.minute)
      };
    } catch (e) {
      var now = new Date();
      return { today: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12), mins: now.getHours() * 60 + now.getMinutes() };
    }
  }

  function estPlus(date, n) {
    var d = new Date(date.getTime());
    d.setDate(d.getDate() + n);
    d.setHours(12, 0, 0, 0);
    return d;
  }

  function estISO(d) {
    return d.getFullYear() + '-' + (d.getMonth() < 9 ? '0' : '') + (d.getMonth() + 1) + '-' + (d.getDate() < 10 ? '0' : '') + d.getDate();
  }

  function estOrdinal(n) {
    var rem = n % 100;
    if (rem >= 11 && rem <= 13) return n + 'th';
    return n + (['th', 'st', 'nd', 'rd'][n % 10] || 'th');
  }

  function estimateDate(rules, today, lead) {
    for (var i = lead; i <= rules.horizon; i++) {
      var d = estPlus(today, i);
      if (rules.closedDates.indexOf(estISO(d)) !== -1) continue;
      if (rules.closedDays.indexOf(d.getDay()) !== -1) continue;
      if (d.getDay() === 1 && rules.mondayNotice > 0) {
        if (rules.fresh) continue;
        if (i < rules.mondayNotice) continue;
      }
      return d;
    }
    return null;
  }

  function list(raw) {
    return String(raw || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function paintEstimate(el) {
    var target = el.querySelector('[data-ibc-delivery-estimate-text]') || el;
    var rules = {
      closedDays: list(el.getAttribute('data-closed-days')).map(Number),
      closedDates: list(el.getAttribute('data-closed-dates')),
      mondayNotice: parseInt(el.getAttribute('data-monday-notice'), 10) || 0,
      fresh: el.getAttribute('data-fresh') === '1',
      horizon: parseInt(el.getAttribute('data-horizon'), 10) || 60
    };
    var lead = parseInt(el.getAttribute('data-lead'), 10);
    if (isNaN(lead) || lead < 0) lead = 1;
    var cutoff = minutesOf(el.getAttribute('data-cutoff'));
    var now = londonNow();
    var beforeCutoff = cutoff === null || now.mins < cutoff;

    var date = estimateDate(rules, now.today, beforeCutoff ? lead : lead + 1);
    if (!date) return;

    /* THE AWAY ROW, only when the away dates are what pushed this date
     * back: the same estimate without them would have been sooner. */
    var awayRow = el.closest('ul') && el.closest('ul').querySelector('[data-ibc-delivery-away]');
    if (awayRow) {
      var away = list(el.getAttribute('data-away-dates'));
      var hit = false;
      if (away.length) {
        var plain = Object.assign({}, rules, {
          closedDates: rules.closedDates.filter(function (d) { return away.indexOf(d) === -1; })
        });
        var sooner = estimateDate(plain, now.today, beforeCutoff ? lead : lead + 1);
        hit = !!sooner && estISO(sooner) !== estISO(date);
      }
      awayRow.hidden = !hit;
    }

    var words = EST_DAYS[date.getDay()] + ' ' + estOrdinal(date.getDate()) + ' ' + EST_MONTHS[date.getMonth()];
    var tomorrow = estISO(date) === estISO(estPlus(now.today, 1));
    var text = 'Estimated delivery ' + (tomorrow ? 'tomorrow, ' + words : words);

    /* "order by 11am" only when it changes the answer: on a Saturday with a
     * cake, ordering at 9am or 3pm both mean Tuesday. */
    if (cutoff !== null && beforeCutoff) {
      var later = estimateDate(rules, now.today, lead + 1);
      if (later && estISO(later) !== estISO(date)) {
        var h = Math.floor(cutoff / 60), m = cutoff % 60;
        var clock = ((h % 12) || 12) + (m ? ':' + (m < 10 ? '0' : '') + m : '') + (h < 12 ? 'am' : 'pm');
        text += ', if you order by ' + clock;
      }
    }
    text += '.';
    if (target.textContent !== text) target.textContent = text;
  }

  function paintDeliveryPromise(scope) {
    var root = scope || document;
    if (!root.querySelectorAll) return;

    Array.prototype.forEach.call(root.querySelectorAll('[data-ibc-delivery-estimate]'), paintEstimate);

    var rows = root.querySelectorAll('[data-ibc-delivery-sameday]');
    Array.prototype.forEach.call(rows, function (row) {
      var cutoff = minutesOf(row.getAttribute('data-cutoff'));
      if (cutoff === null) return;

      var now = new Date();
      var mins = now.getHours() * 60 + now.getMinutes();
      var past = mins >= cutoff;

      /* Idempotent. The theme editor can fire section:load repeatedly and
         the original sentence has to survive every one of them, so it is
         stashed on first run rather than read back off the element. */
      swap(row.querySelector('[data-ibc-delivery-name]'), row.getAttribute('data-past-name'), past);
      swap(row.querySelector('[data-ibc-delivery-note]'), row.getAttribute('data-past-note'), past);

      row.classList.toggle('is-past', past);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      paintDeliveryPromise(document);
    });
  } else {
    paintDeliveryPromise(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    paintDeliveryPromise(event.target || document);
  });

  /* A VARIANT CHANGE OR A QUICK VIEW DRAWS A FRESH DELIVERY BLOCK without
   * firing section:load, and its estimate would sit at the no-script
   * sentence. Painting whatever estimate appears keeps it current. */
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) {
          Array.prototype.forEach.call(document.querySelectorAll('[data-ibc-delivery-estimate]'), paintEstimate);
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.IbcDeliveryPromise = { paint: paintDeliveryPromise };
})();

/* =========================================================================
   THE CONTENTS LINE — SOFTENING THE JUMP
   =========================================================================
   "About / Allergens / Size / Delivery" under the product title is a plain
   `<a href="#...">` and stays one: it works with this script absent, it can
   be middle-clicked, and the browser's own fragment handling already puts
   the Information section in the right place (assets/ibc-product.css gives
   it a `scroll-margin-top` clearing the sticky header).

   All this adds is a smooth landing, and only for a visitor who has not asked
   for stillness. It is NOT done with `html { scroll-behavior: smooth }`,
   which is the obvious way: assets/ibc-base.css sets that property to `auto`
   inside a reduced-motion query, and ibc-product.css loads after it at equal
   specificity, so a CSS rule here would quietly beat the accessibility guard.
   Read the preference instead of out-specifying it.

   Every path that cannot do the nice thing falls through to the default,
   which is the browser doing the plain thing correctly.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  function initInfoLink(scope) {
    var root = scope || document;
    if (!root.querySelectorAll) return;

    Array.prototype.forEach.call(
      root.querySelectorAll('.ibc-pdp__info-link'),
      function (link) {
        if (link.hasAttribute('data-ibc-smooth')) return;
        link.setAttribute('data-ibc-smooth', '');

        link.addEventListener('click', function (event) {
          if (event.defaultPrevented) return;
          /* Leave every "open somewhere else" gesture to the browser. */
          if (event.button !== 0) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

          if (!window.matchMedia || !window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;

          var id = (link.getAttribute('href') || '').slice(1);
          if (!id) return;
          var target = document.getElementById(id);
          if (!target || !target.scrollIntoView) return;

          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });

          /* The fragment still belongs in the address bar — someone who
             scrolled here should be able to share or reload the same view.
             `replaceState` rather than setting `location.hash`, which would
             scroll a second time and undo the smooth one. */
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', '#' + id);
          }

          /* Focus follows the eye, or a keyboard user lands back at the top
             of the document on their next Tab. `tabindex="-1"` makes a
             non-interactive section focusable without adding it to the tab
             order; `preventScroll` stops the browser jumping to it and
             cancelling the animation that is still running. */
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          try {
            target.focus({ preventScroll: true });
          } catch (error) {
            /* Older browsers reject the options object. The scroll already
               happened; losing focus placement is not worth a broken link. */
          }
        });
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initInfoLink(document);
    });
  } else {
    initInfoLink(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initInfoLink(event.target || document);
  });
})();
